import React, { useState, useCallback, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import {
  Box,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { IconFilter, IconFilterOff } from '@tabler/icons-react';
import App from "../App"
import CategoryMenu from "../components/CategoryMenu";
import { fetchApi } from "../components/FetchApi";
import { getCategoryAttributes } from "../services/CategoryAttributeService";
import { getPromotedListings, searchListings } from "../services/ListingService";
import ListingCard from "../components/ListingCard"
import CustomPagination from "../components/CustomPagination";
import { useSearchParams } from 'react-router-dom';
import { useCategories } from "../context/CategoriesContext";
import {
  getAllCategoryIdsFromTree,
  findCategoryInTree,
  collectStructuredCategoryAttributeIds,
} from "../utils/categoryUtils";
import CategoryAttribute from "../components/filters/CategoryAttribute"
import SearchBar from "../components/SearchBar";
import LoadingComponent from "../components/LoadingComponent"
import {
  hasMeaningfulSearchParams,
  parseAttributeFiltersFromSearchParams,
  parseOptionalPositiveFloat,
  deleteAttributeFilterParamsFromSearchParams,
} from "../utils/searchUrlUtils";

const sortingOptions = [{id: 1, column: "price", order: "asc", name: "cena: najniższa"}, {id: 2, column: "price", order: "desc", name: "cena: najwyższa"}, {id: 3, column: "date_from", order: "asc", name: "data: najstarsza"}, {id: 4, column: "date_from", order: "desc", name: "data: najnowsza"}]

const parseCategoriesParam = (paramValue) => {
  if (!paramValue) return [];
  return paramValue
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
    .map((value) => {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? value : parsed;
    });
};
const parseSortParam = (paramValue) => {
  if (!paramValue) return [];
  return paramValue
    .split(',')
    .map((value) => value.trim())
    .filter(Boolean)
    .map((value) => {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? value : parsed;
    });
};
/** Returns the primary (first) category ID from an array of category IDs, or null. */
const getSelectedCategory = (categoryIds) => {
  return Array.isArray(categoryIds) && categoryIds.length > 0 ? categoryIds[0] : null;
};
/** Returns true when two category-id lists contain the same ids (order ignored). */
const areCategoryIdListsEquivalent = (left, right) => {
  if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
    return false;
  }
  const normalize = (ids) => [...ids].map((id) => Number(id)).sort((a, b) => a - b);
  const a = normalize(left);
  const b = normalize(right);
  return a.every((id, index) => id === b[index]);
};

/** True when structured category-attribute definitions are present in state. */
const hasLoadedCategoryAttributeDefinitions = (definitions) =>
  Boolean(
    definitions &&
      typeof definitions === "object" &&
      !Array.isArray(definitions) &&
      Array.isArray(definitions.categoryAttributesDTO) &&
      definitions.categoryAttributesDTO.length > 0
  );

function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const meaningfulUrl = hasMeaningfulSearchParams(searchParams);
  const querySearchString = searchParams.get('q') || "";
  const queryCategoryIds = parseCategoriesParam(searchParams.get('categories'));
  const querySortParts = parseSortParam(searchParams.get('sort'));
  const queryPageParsed = parseInt(searchParams.get('page'), 10);

  const initialSearchString = meaningfulUrl ? querySearchString : "";
  const initialCategory =
    meaningfulUrl && queryCategoryIds.length > 0 ? queryCategoryIds : null;
  const initialCatAttrs = meaningfulUrl
    ? parseAttributeFiltersFromSearchParams(searchParams)
    : [];
  const initialPrice = meaningfulUrl
    ? {
        priceMin: parseOptionalPositiveFloat(searchParams.get('priceMin')),
        priceMax: parseOptionalPositiveFloat(searchParams.get('priceMax')),
      }
    : { priceMin: null, priceMax: null };
  const initialSort =
    meaningfulUrl &&
    Array.isArray(querySortParts) &&
    querySortParts.length >= 2
      ? { column: String(querySortParts[0]), order: String(querySortParts[1]) }
      : { column: sortingOptions[3].column, order: sortingOptions[3].order };
  const initialPageNum =
    meaningfulUrl && !Number.isNaN(queryPageParsed) && queryPageParsed > 0
      ? queryPageParsed
      : 1;
  const [catAttributes, setCatAttributesData] = useState(null);
  const [selectedCategories, setSelectedCategories] = useState(initialCategory);
  const [listings, setListings] = useState(null);
  const [isListLoading, setIsListLoading] = useState(false);
  // Fetch promoted listings for display when no selectedCategories selected and no results
  const [promotedListings, setPromotedListings] = useState(null);
  const [isPromotedLoading, setIsPromotedLoading] = useState(false);
  // Use isListLoading to show loading state during search
  const [catAttrs, setCatAttributes] = useState(initialCatAttrs);
  const [{priceMin, priceMax}, setPrice] = useState(initialPrice);
  const [searchString, setSearchString] = useState(initialSearchString);
  const [sort, setSort] = useState(initialSort);
  const [pageNum, setPage] = useState(initialPageNum);
  const [hydrationReady, setHydrationReady] = useState(false);
  const searchStringRef = useRef(initialSearchString);
  useLayoutEffect(() => {
    setHydrationReady(true);
  }, []);

  // Let restore effect filter URL-parsed filters once category attributes are loaded (mount URL only)
  useLayoutEffect(() => {
    const attrs = parseAttributeFiltersFromSearchParams(searchParams);
    if (attrs.length > 0) {
      pendingCatAttrsRef.current = attrs;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally first paint only (pasted URL)
  }, []);

  // Sync ref with searchString state
  useEffect(() => {
    searchStringRef.current = searchString;
  }, [searchString]);
  // Fetch all categories for count calculations
  const { categories } = useCategories();

 const updateSearchParams = useCallback((params) => {
  setSearchParams(prev => {
    const next = new URLSearchParams(prev);

    let resetPage = false;
    // --- filtry ---
    if ("q" in params) {
      const prevValue = prev.get("q");
      const nextValue = params.q?.trim() || null;
      if (prevValue !== nextValue) resetPage = true;
      if (nextValue) next.set("q", nextValue);
      else next.delete("q");
    }

    if ("categories" in params) {
      const prevValue = prev.get("categories");
      const nextValue =
        Array.isArray(params.categories) && params.categories.length > 0
          ? params.categories.map(String).join(",")
          : null;

      if (prevValue !== nextValue) resetPage = true;
      if (nextValue) next.set("categories", nextValue);
      else next.delete("categories");
    }

    if ("priceMin" in params) {
      const prevValue = prev.get("priceMin");
      const nextValue =
        typeof params.priceMin === "number" && params.priceMin > 0
          ? String(params.priceMin)
          : null;
      if (prevValue !== nextValue) resetPage = true;
      if (nextValue) next.set("priceMin", nextValue);
      else next.delete("priceMin");
    }

    if ("priceMax" in params) {
      const prevValue = prev.get("priceMax");
      const nextValue =
        typeof params.priceMax === "number" && params.priceMax > 0
          ? String(params.priceMax)
          : null;
      if (prevValue !== nextValue) resetPage = true;
      if (nextValue) next.set("priceMax", nextValue);
      else next.delete("priceMax");
    }
    if ("filters" in params) {
      if (Array.isArray(params.filters) && params.filters.length > 0) {
        params.filters.forEach((filter) => {
          const prevValue = prev.get(filter.categoryAttributeId);
          let nextValue = null;
          if(filter.itemAttributeValues )
          {
            nextValue = filter.itemAttributeValues.length > 0 ? filter.itemAttributeValues.map(String).join(",") : null;
          } else if (filter.userAttributeValues && filter.userAttributeValues !== null) {
            if(filter.userAttributeValues.valueFrom !== null || filter.userAttributeValues.valueTo !== null) {
              nextValue = `[${filter.userAttributeValues.valueFrom? filter.userAttributeValues.valueFrom : 0}, ${filter.userAttributeValues.valueTo? filter.userAttributeValues.valueTo: 0}, ${filter.userAttributeValues.type}]`
            }
            else if(filter.userAttributeValues.value && filter.userAttributeValues.value !== null) {
              nextValue = `[${filter.userAttributeValues.value? filter.userAttributeValues.value : 0}, ${filter.userAttributeValues.type}]`
            } else {
              nextValue = null;
            }
          }
           
        if (prevValue !== nextValue) resetPage = true;
        if (nextValue) next.set(filter.categoryAttributeId, nextValue);
        else next.delete(filter.categoryAttributeId);
          })
      } else {
        for (const key of [...prev.keys()]) {
          if (!/^\d+$/.test(key)) continue;
          if (prev.get(key) != null) resetPage = true;
        }
        deleteAttributeFilterParamsFromSearchParams(next);
      }
    }
    // --- sort ---
    if ("sort" in params) {
      const prevValue = prev.get("sort");
      const nextValue = params.sort.column + "," + params.sort.order || null;
      if (prevValue !== nextValue) resetPage = true;
      if (nextValue) next.set("sort", nextValue);
      else next.set("sort", "date_from,desc");
    }

    // --- reset page jeśli filtry lub sort się zmieniły ---
    if (resetPage) {
      next.set("page", "1");
    }

    // --- page (ręczne ustawienie bez resetu) ---
    if ("page" in params) {
      const pageValue =
        typeof params.page === "number" && params.page > 0
          ? String(params.page)
          : 1;
      if (pageValue) next.set("page", pageValue);
      else next.delete("page");
    }

    // --- brak realnej zmiany → brak update ---
    if (next.toString() === prev.toString()) {
      return prev;
    }
    next.sort();
    return next;
  }, { replace: true });
}, [setSearchParams]);

      // Ref for debounce timer
      const debounceTimerRef = useRef(null);
      
     /* // Ref to store latest values for performSearch to avoid recreating function
      const searchParamsRef = useRef({ priceMin, priceMax, pageNum, sort, catAttrs, selectedCategories });
      */
      // Ref to SearchAutocomplete component to control it from parent
      const searchAutocompleteRef = useRef(null);

      // Ref to store the last search session ID for updating searches
      const lastSearchSessionIdRef = useRef(null);
      
      // Ref for AbortController for trackOrUpdateSearch requests
      const trackSearchAbortControllerRef = useRef(null);
      
      // Ref for AbortController for updateSearchHistoryWithResults requests
      const updateSearchHistoryAbortControllerRef = useRef(null);
      
  // Ref to store pending catAttrs to restore after catAttributes loads
  const pendingCatAttrsRef = useRef(null);
  // Ref to prevent repeated selectedCategories attribute fetches for same selectedCategories
  const lastSelectedCategoryIdRef = useRef(null);
  const lastFetchedCategoryIdRef = useRef(null);
  const lastPriceMinRef = useRef(null);
  const lastPriceMaxRef = useRef(null);

  /** Get all category IDs for a parent category and its descendants from the tree. */
  const getAllCategoryIds = useCallback(
    (parentCategoryId) => getAllCategoryIdsFromTree(categories, parentCategoryId),
    [categories]
  );

          /**
           * Fetch listing counts with current filters
           * When no selectedCategories is selected, fetch counts for all categories
           * Uses debounce to avoid too frequent calls
           */
   /*       const updateListingCounts = useCallback(() => {
            // Clear existing debounce timer
            if (updateListingCountsDebounceTimerRef.current) {
              clearTimeout(updateListingCountsDebounceTimerRef.current);
            }
            
            // Debounce the actual call
            updateListingCountsDebounceTimerRef.current = setTimeout(() => {
              // Build SearchParamsDTO payload similar to search request
              // Filter out empty filters before sending
              const filteredCatAttrsForCounts = catAttrs ? catAttrs.filter(attr => {
                // Keep filter if it has itemAttributeValues
                if (attr.itemAttributeValues && attr.itemAttributeValues.length > 0) {
                  return true;
                }
                // Keep filter if it has non-empty userAttributeValues
                if (attr.userAttributeValues) {
                  const hasUserValues = Object.values(attr.userAttributeValues).some(
                    val => val !== null && val !== "" && val !== undefined
                  );
                  if (hasUserValues) {
                    return true;
                  }
                }
                // Remove filter if both are empty
                return false;
              }) : [];
              
              const payload = {
                attributeFilterDTO: filteredCatAttrsForCounts.length > 0 ? filteredCatAttrsForCounts : null
              };
              
             
              // Add searchString if present (use ref to get current value)
              if (searchStringRef.current) {
                categoryParam += categoryParam ? `&q=${encodeURIComponent(searchStringRef.current)}` : `?q=${encodeURIComponent(searchStringRef.current)}`;
              }
              
              const categoryIdParam = categoryParam.replace('?categories=', '').split('&')[0] || null;
              const searchParam = searchStringRef.current || null;

              setIsCountsLoading(true);
              getCategoryCounts({
                categoryId: categoryIdParam,
                searchString: searchParam,
                payload
              })
                .then(({ data, meta }) => {
                  if (meta) {
                    console.warn('Listing counts warning:', meta);
                  }
                  setListingCounts(data || []);
                })
                .catch((err) => {
                  console.error('Error fetching listing counts:', err);
                  setListingCounts([]);
                })
                .finally(() => {
                  setIsCountsLoading(false);
                });
            }, 500); // 500ms debounce
          }, [categoryParamsId, selectedCategories, priceMin, priceMax, catAttrs]);

          // Create a map of selectedCategories IDs to listing counts
          const categoryCountMap = useMemo(() => {
            if (!listingCounts || !categories) return {};
        
            // First, create a basic map of direct counts
            const directCountMap = listingCounts.reduce((map, item) => {
              map[item.categoryId] = item.count;
              return map;
            }, {});
        
            // Function to calculate total count for a selectedCategories including all its children
            const calculateTotalCount = (selectedCategories) => {
              // Start with direct count or 0 if none
              let totalCount = directCountMap[selectedCategories.id] || 0;
        
              // Add counts from all children recursively
              if (selectedCategories.children && selectedCategories.children.length > 0) {
                selectedCategories.children.forEach(child => {
                  totalCount += calculateTotalCount(child);
                });
              }
        
              return totalCount;
            };
        
            // Create the final map with total counts
            const totalCountMap = {};
            if (categories) {
              // Process all categories recursively to ensure we have counts for all categories
              const processCategory = (cat) => {
                totalCountMap[cat.id] = calculateTotalCount(cat);
        
                // Process children recursively
                if (cat.children && cat.children.length > 0) {
                  cat.children.forEach(child => processCategory(child));
                }
              };
        
              // Start with root categories
              categories.forEach(selectedCategories => processCategory(selectedCategories));
            }
        
            return totalCountMap;
          }, [listingCounts, categories]);
*/
  const resolvedListingsTotalPages = useMemo(() => {
    if (typeof listings?.totalPages === 'number') {
      return listings.totalPages;
    }
    const totalElements = listings?.totalElements;
    const size = listings?.size;
    if (typeof totalElements === 'number' && typeof size === 'number' && size > 0) {
      return Math.ceil(totalElements / size);
    }
    return 1;
  }, [listings]);

  const resolvedListingsPage = typeof listings?.number === 'number' ? listings.number : pageNum;

  const resolvedPromotedTotalPages = useMemo(() => {
    if (typeof promotedListings?.totalPages === 'number') {
      return promotedListings.totalPages;
    }
    const totalElements = promotedListings?.totalElements;
    const size = promotedListings?.size;
    if (typeof totalElements === 'number' && typeof size === 'number' && size > 0) {
      return Math.ceil(totalElements / size);
    }
    return 1;
  }, [promotedListings]);

  const resolvedPromotedPage = typeof promotedListings?.number === 'number' ? promotedListings.number : 1;

  // Function to perform search with current filters
  useEffect(() => {
    setIsListLoading(true);
    const searchParamsString =  searchParams.toString();

    // Persist current search context for recommendations on listing detail page
    // (so "Podobne ogłoszenia" can prefer category/filters chosen by the user)
    try {
      localStorage.setItem('lastSearchParams', searchParamsString);
    } catch (e) {
      // ignore storage errors (private mode / quota)
    }

    searchListings(
     searchParamsString
    )
      .then(({ data, meta }) => {
        if (meta) {
          console.warn('Search listings warning:', meta);
        }
        setListings(data || []);
      })
      .catch((err) => {
        console.error('Error searching listings:', err);
        setListings([]);
      })
      .finally(() => {
        setIsListLoading(false);
      });
  }, [searchParams]);

  // Update search history with result count when listings are loaded
  // This updates the resultCount for the search that was already tracked
  useEffect(() => {
    if (!isListLoading && listings && lastSearchSessionIdRef.current) {
      const updateSearchHistoryWithResults = async () => {
        // Cancel any previous update request
        if (updateSearchHistoryAbortControllerRef.current) {
          updateSearchHistoryAbortControllerRef.current.abort();
        }
        
        // Create new AbortController for this request
        updateSearchHistoryAbortControllerRef.current = new AbortController();
        const signal = updateSearchHistoryAbortControllerRef.current.signal;
        
        try {
          // Get or create session ID
          let sessionId = localStorage.getItem('viewSessionId');
          if (!sessionId) {
            sessionId = 'session_' + Math.random().toString(36).substring(2, 15);
            localStorage.setItem('viewSessionId', sessionId);
          }
          
          // Extract selectedCategories IDs
         // const categoryIds = selectedCategories ? getAllCategoryIds(selectedCategories) : null;
          const categoryIds = selectedCategories ? selectedCategories : null;
          // Filter out empty filters
          const filteredCatAttrs = catAttrs ? catAttrs.filter(attr => {
            if (attr.itemAttributeValues && attr.itemAttributeValues.length > 0) {
              return true;
            }
            if (attr.userAttributeValues) {
              const hasUserValues = Object.values(attr.userAttributeValues).some(
                val => val !== null && val !== "" && val !== undefined
              );
              if (hasUserValues) {
                return true;
              }
            }
            return false;
          }) : [];
          
          // Update search history with result count
          const searchHistoryData = {
            searchQuery: searchStringRef.current || null,
            categoryIds: categoryIds,
            priceMin: priceMin > 0 ? priceMin : null,
            priceMax: priceMax > 0 ? priceMax : null,
            filters: filteredCatAttrs.length > 0 ? JSON.stringify(filteredCatAttrs) : null,
            resultCount: listings?.totalElements || 0
          };
          
          const response = await fetchApi(`/behavior/search/${lastSearchSessionIdRef.current}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(searchHistoryData),
            signal: signal
          });
          
          // Check if request was aborted
          if (signal.aborted) {
            return;
          }
          if (response?.meta) {
            console.warn('Search results tracking warning:', response.meta);
          }
        } catch (error) {
          // Ignore aborted requests
          if (error.name === 'AbortError' || signal?.aborted) {
            return;
          }
          console.error('Error updating search history with results:', error);
        }
      };
      
      // Only update if we have a search query (not just selectedCategories/filter changes)
      if (searchStringRef.current && searchStringRef.current.trim()) {
        updateSearchHistoryWithResults();
      }
    }
    
    // Cleanup function
    return () => {
      if (updateSearchHistoryAbortControllerRef.current) {
        updateSearchHistoryAbortControllerRef.current.abort();
      }
    };
  }, [listings, isListLoading, selectedCategories, catAttrs, priceMin, priceMax]);

  const resolveCategoryFromSuggestion = useCallback((suggestion) => {
    if (!suggestion) return null;
    if (categories && categories.length > 0) {
      const foundCategory = findCategoryInTree(categories, suggestion.categoryId);
      if (foundCategory) {
        return foundCategory;
      }
    }
    return {
      id: suggestion.categoryId,
      name: suggestion.categoryName,
      parentId: null,
      children: []
    };
  }, [categories]);

  const resolveCategoriesParam = useCallback((suggestion = null) => {
    if (suggestion) {
      const selectedCategory = resolveCategoryFromSuggestion(suggestion);
      return selectedCategory ? selectedCategories : [];
    }
    if (selectedCategories) {
      return selectedCategories;
    }
    return [];
  }, [resolveCategoryFromSuggestion, selectedCategories]);

  // Update listings when filters change (with debounce for price and attributes, but NOT for searchString or pageNum)
  useEffect(() => {
    if (!hydrationReady) return;
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    // Debounce filter changes (priceMin, priceMax, catAttrs) - 300ms delay
    debounceTimerRef.current = setTimeout(() => {
      updateSearchParams({
            priceMin: priceMin,
            priceMax: priceMax,
            filters: catAttrs ?? [],
          });
    }, 300);
    
    // Cleanup timer on unmount or when dependencies change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [hydrationReady, priceMin, priceMax, catAttrs, updateSearchParams]);
  // Update listings when pageNum changes (immediate, no debounce)
  useEffect(() => {
    if (!hydrationReady) return;
       updateSearchParams({
      categories: selectedCategories,
      page: pageNum,
      sort: sort,
    });
    
  }, [hydrationReady, pageNum, selectedCategories, sort, updateSearchParams]);

  const selectedCategoryId = useMemo(
    () => getSelectedCategory(selectedCategories),
    [selectedCategories]
  );

  // Fetch category attribute definitions for the primary selected category
  useEffect(() => {
    if (!selectedCategoryId) {
      lastFetchedCategoryIdRef.current = null;
      setCatAttributesData((prev) =>
        hasLoadedCategoryAttributeDefinitions(prev) ? null : prev
      );
      return;
    }
    if (lastFetchedCategoryIdRef.current === selectedCategoryId) {
      return;
    }
    lastFetchedCategoryIdRef.current = selectedCategoryId;
    getCategoryAttributes(selectedCategoryId)
      .then(({ data, meta }) => {
        if (meta) {
          console.warn('Search selectedCategories attributes warning:', meta);
        }
        const nextDefinitions = data || null;
        setCatAttributesData(nextDefinitions);
        if (!hasLoadedCategoryAttributeDefinitions(nextDefinitions)) {
          lastFetchedCategoryIdRef.current = null;
        }
      })
      .catch((err) => {
        console.error('Error fetching selectedCategories attributes:', err);
        setCatAttributesData(null);
        lastFetchedCategoryIdRef.current = null;
      })
      .finally(() => {
      });
  }, [selectedCategoryId]);

  // Fetch promoted listings when no selectedCategories is selected and no search results
  useEffect(() => {
    if (getSelectedCategory(selectedCategories) === null && !isListLoading && (!listings?.content || listings.content.length === 0)) {
      setIsPromotedLoading(true);
      getPromotedListings({ page: 0, size: 12 })
        .then(({ data, meta }) => {
          if (meta) {
            console.warn('Promoted listings warning:', meta);
          }
          setPromotedListings(data);
        })
        .catch((err) => {
          console.error('Error fetching promoted listings:', err);
          setPromotedListings(null);
        })
        .finally(() => {
          setIsPromotedLoading(false);
        });
    }
  }, [selectedCategories, listings, isListLoading]);

  // Restore catAttrs when catAttributes (from API) are loaded
  useEffect(() => {
    const pending = pendingCatAttrsRef.current;
    if (!pending?.length || !hasLoadedCategoryAttributeDefinitions(catAttributes)) {
      return;
    }

    const availableAttributeIds = collectStructuredCategoryAttributeIds(catAttributes);
    const filteredCatAttrs = pending.filter((savedAttr) =>
      availableAttributeIds.some(
        (id) => Number(id) === Number(savedAttr.categoryAttributeId)
      )
    );

    if (filteredCatAttrs.length > 0) {
      setCatAttributes(filteredCatAttrs);
    } else {
      setCatAttributes(pending);
    }

    pendingCatAttrsRef.current = null;
  }, [catAttributes]);

  // Cleanup on unmount - abort all pending requests and clear timers
  useEffect(() => {
    const trackAbortController = trackSearchAbortControllerRef.current;
    const updateAbortController = updateSearchHistoryAbortControllerRef.current;
    const debounceTimer = debounceTimerRef.current;
    return () => {
      // Abort all pending requests
      if (trackAbortController) {
        trackAbortController.abort();
      }
      if (updateAbortController) {
        updateAbortController.abort();
      }
      
      // Clear all timers
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, []);


  const handleCategorySelect = useCallback((categoryId, allCategoryIds) => {
    const currentCategoryId = getSelectedCategory(selectedCategories);
    const sameLogicalCategory =
      categoryId != null &&
      currentCategoryId != null &&
      Number(categoryId) === Number(currentCategoryId);

    if (categoryId && sameLogicalCategory) {
      lastSelectedCategoryIdRef.current = categoryId;
      const expandedIds =
        Array.isArray(allCategoryIds) && allCategoryIds.length > 0
          ? allCategoryIds
          : getAllCategoryIds(categoryId);
      if (
        expandedIds.length > 0 &&
        !areCategoryIdListsEquivalent(selectedCategories, expandedIds)
      ) {
        setSelectedCategories(expandedIds);
      }
      return;
    }

    if (categoryId && categoryId === lastSelectedCategoryIdRef.current && categoryId === currentCategoryId) {
      return;
    }
    lastSelectedCategoryIdRef.current = categoryId;
    if (!categoryId) {
      if (currentCategoryId == null) {
        return;
      }
      setCatAttributesData((prev) =>
        hasLoadedCategoryAttributeDefinitions(prev) ? null : prev
      );
      setCatAttributes([]);
      setPrice({ priceMin: null, priceMax: null });
      lastPriceMinRef.current = null;
      lastPriceMaxRef.current = null;
      pendingCatAttrsRef.current = null;
      setSelectedCategories(null);
    } else {
      pendingCatAttrsRef.current = catAttrs && catAttrs.length > 0 ? catAttrs : null;
      setCatAttributesData(null);
      lastFetchedCategoryIdRef.current = null;
      const ids = Array.isArray(allCategoryIds) && allCategoryIds.length > 0
        ? allCategoryIds
        : getAllCategoryIds(categoryId);
      setSelectedCategories(ids);
    }
  }, [catAttrs, getAllCategoryIds, selectedCategories]);
  
  
  
    const handleCategoryAttributeChanged = useCallback((categoryAttributesList) => {
      if (categoryAttributesList === catAttrs) {
        return;
      }
      setCatAttributes(categoryAttributesList); // Update state
    }, [catAttrs]);
  
    const handlePriceMinChange = useCallback((value) => {
      if (value && value === lastPriceMinRef.current && value === priceMin) {
        return;
      }
      lastPriceMinRef.current = value > 0 ? value : null;

      setPrice((prev) => ({
        ...prev,
        priceMin: lastPriceMinRef.current
      }));
    }, [priceMin]);

     const handlePriceMaxChange = useCallback((value) => {
      if (value && value === lastPriceMaxRef.current && value === priceMax) {
        return;
      }
      lastPriceMaxRef.current = value > 0 ? value : null;
      setPrice((prev) => ({
        ...prev,
        priceMax: lastPriceMaxRef.current,
      }));
    }, [priceMax]);
    
    // Function to clear all filters
    const handleClearAllFilters = useCallback(() => {
      setCatAttributes(prev => prev.map(attr => ({...attr, itemAttributeValues: null, userAttributeValues: {valueFrom: null, valueTo: null, value: null, type: null}})));
      setPrice({ priceMin: null, priceMax: null });
      // Reset will trigger search automatically via useEffect
    }, []);

    // Check if there are any active filters
    const hasActiveFilters = useMemo(() => {
      // Check if there are selectedCategories attribute filters
      const hasCatAttrFilters = catAttrs && catAttrs.some(attr => {
        if (attr.itemAttributeValues && attr.itemAttributeValues.length > 0) {
          return true;
        }
        if (attr.userAttributeValues) {
          return Object.values(attr.userAttributeValues).some(
            val => val !== null && val !== "" && val !== undefined
          );
        }
        return false;
      });
      
      // Check if there are price filters
      const hasPriceFilters = (priceMin && priceMin > 0) || (priceMax && priceMax > 0);
      
      return hasCatAttrFilters || hasPriceFilters;
    }, [catAttrs, priceMin, priceMax]);
  
    const handlePageChange = useCallback((page) => {
      setPage(page);
      window.scrollTo(0, 0);
    }, []);

    const handleSearch = useCallback(() => {
      // Cancel any pending autocomplete requests first
      if (searchAutocompleteRef.current && searchAutocompleteRef.current.cancelRequests) {
        searchAutocompleteRef.current.cancelRequests();
      }
      
      // Close autocomplete if open
      if (searchAutocompleteRef.current) {
        searchAutocompleteRef.current.close();
        searchAutocompleteRef.current.blur();
      }
      /*if (value && value === searchString) {
       return;
      }*/
      //lastPriceMaxRef.current = value > 0 ? value : null;
     // setSearchString(value);
      //wyszukiwarka
        updateSearchParams({
          q: searchStringRef.current
        });
    }, [updateSearchParams]);
  
    const handleSortChange = useCallback((event) => {
      const selectedSort = sortingOptions.find((option) => option.column + "," + option.order === event.target.value);
      setSort(selectedSort);
    }, [])

  return (
    <App>
      <Container maxWidth="xl">
        <Stack spacing={{ xs: 2, sm: 3 }} sx={{ py: { xs: 2, sm: 3 } }}>
          {/* Category Menu */}
          <Box
            sx={{
              display: "flex",
              justifyContent: { xs: "center", sm: "flex-start" },
              alignItems: "center",
              px: { xs: 1, sm: 2 },
            }}
          >
            <CategoryMenu
              onCategorySelect={handleCategorySelect}
              startingPointId={getSelectedCategory(selectedCategories)}
             // externalCategoryCountMap={categoryCountMap}
            />
          </Box>

          {/* Search Section */}
          <SearchBar
            value={searchString}
            onChange={(event, newValue) => {
              setSearchString(newValue || '');
              searchStringRef.current = newValue || '';
              if (newValue === '') {
                 handleSearch();
              }
            }}
            onEnter={(searchValue) => {
              handleSearch();
            }}
            onSelect={(suggestion) => {
              const selectedValue = suggestion?.title || '';
              setSearchString(selectedValue);
              searchStringRef.current = selectedValue;

              /*const selectedCategory = resolveCategoryFromSuggestion(suggestion);
              const id = selectedCategory?.id ?? null;
              const ids = id != null ? getAllCategoryIds(id) : [];
              handleCategorySelect(id, ids);*/
              //performSearch();
               handleSearch();
            }}
            onSearch={(searchValue) => {
              handleSearch();
            }}
            isLoading={isListLoading}
            isMobile={isMobile}
            searchAutocompleteRef={searchAutocompleteRef}
            resolveCategoriesParam={resolveCategoriesParam}
            placeholder="Wyszukaj"
          />

          {/* Main Content Grid */}
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
            {/* Results Section */}
            <Grid size={{ xs: 12, md: 9 }} order={{ xs: 2, md: 1 }}>
              {isListLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, gap: 2 }}>
                  <LoadingComponent />
                  <Typography variant="body1">Wyszukiwanie...</Typography>
                </Box>
              ) : listings?.content?.length > 0 ? (
                <>
                  <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
                    {listings.content.map((listing) => (
                      <ListingCard key={listing.id} listing={listing}/>
                    ))}
                  </Grid>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <CustomPagination
                      totalPages={resolvedListingsTotalPages}
                      currentPage={resolvedListingsPage}
                      onPageChange={handlePageChange}
                    />
                  </Box>
                </>
              ) : getSelectedCategory(selectedCategories) === null ? (
                isPromotedLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4, gap: 2 }}>
                    <LoadingComponent />
                    <Typography variant="body1">Ładowanie promowanych ogłoszeń...</Typography>
                  </Box>
                ) : promotedListings?.content?.length > 0 ? (
                  <>
                    <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: 3 }}>
                      {promotedListings.content.map((listing) => (
                        <ListingCard key={listing.id} listing={listing}/>
                      ))}
                    </Grid>
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                      <CustomPagination
                        totalPages={resolvedPromotedTotalPages}
                        currentPage={resolvedPromotedPage}
                        onPageChange={(page) => {
                          setIsPromotedLoading(true);
                          getPromotedListings({ page, size: 12 })
                            .then(({ data, meta }) => {
                              if (meta) {
                                console.warn('Promoted listings warning:', meta);
                              }
                              setPromotedListings(data);
                            })
                            .catch((err) => {
                              console.error('Error fetching promoted listings:', err);
                              setPromotedListings(null);
                            })
                            .finally(() => {
                              setIsPromotedLoading(false);
                            });
                        }}
                      />
                    </Box>
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
                    <Typography 
                      variant="h6" 
                      color="text.secondary"
                      sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                    >
                      Brak ogłoszeń spełniających kryteria wyszukiwania
                    </Typography>
                  </Box>
                )
              ) : (
                <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 6 } }}>
                  <Typography 
                    variant="h6" 
                    color="text.secondary"
                    sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                  >
                    Brak ogłoszeń spełniających kryteria wyszukiwania
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* Filters Sidebar */}
            <Grid size={{ xs: 12, md: 3 }} order={{ xs: 1, md: 2 }}>
              <Paper 
                elevation={2}
                sx={{ 
                  p: { xs: 2, sm: 2.5 },
                  position: { md: 'sticky' },
                  top: { md: 80 }, // 64px (AppBar height) + 16px padding
                  maxHeight: { md: 'calc(100vh - 100px)' },
                  overflowY: { md: 'auto' }
                }}
              >
                <Stack spacing={2.5}>
                  {/* Sort */}
                  <FormControl fullWidth size="small">
                    <InputLabel id="sortowanie_select" shrink>
                      Sortowanie
                    </InputLabel>
                    <Select 
                      value={sort.column + "," + sort.order} 
                      onChange={(event) => handleSortChange(event)}
                      label="Sortowanie"
                    >
                      {sortingOptions.map((option) => (
                        <MenuItem key={option.id} value={option.column + "," + option.order}>
                          {option.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Filter Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Filtry
                    </Typography>
                    {hasActiveFilters && (
                      <Tooltip title="Wyczyść wszystkie filtry">
                        <IconButton
                          onClick={handleClearAllFilters}
                          size="small"
                          color="error"
                        >
                          <IconFilterOff size={20} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {!hasActiveFilters && (
                      <Tooltip title="Wybierz filtry">
                          <IconFilter size={20} />
                      </Tooltip>
                    )}
                  </Box>

                  {/* Price Filter */}
                  <FormControl fullWidth>
                    <InputLabel shrink>Cena</InputLabel>
                    <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                      <TextField
                        label="Od"
                        type="number"
                        size="small"
                        fullWidth
                        value={priceMin && priceMin > 0 ? priceMin : ''}
                        onChange={(e) => {
                          const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                          handlePriceMinChange(isNaN(value) ? 0 : value);
                        }}
                        InputProps={{
                          inputProps: { min: 0, step: 0.01 },
                          endAdornment: <InputAdornment position="end">zł</InputAdornment>
                        }}
                      />
                      <TextField
                        label="Do"
                        type="number"
                        size="small"
                        fullWidth
                        value={priceMax && priceMax > 0 ? priceMax : ''}
                        onChange={(e) => {
                          const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                          handlePriceMaxChange(isNaN(value) ? 0 : value);
                        }}
                        InputProps={{
                          inputProps: { min: 0, step: 0.01 },
                          endAdornment: <InputAdornment position="end">zł</InputAdornment>
                        }}
                      />
                    </Stack>
                  </FormControl>

                  {/* Category Attributes */}
                  <CategoryAttribute 
                    categoryAttributesList={catAttributes} 
                    initialValues={catAttrs}
                    onCatAttrChange={handleCategoryAttributeChanged} 
                  />
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
      </Container>
    </App>
  );
}

export default Search;
